// ============================================
// Channel Mixer Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { CHANNEL_MIXER_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Channel Mixer post-processing effect
 * Allows remapping of RGB channels
 */
export class ChannelMixerEffect extends PostProcessEffect {
  readonly type = 'channelMixer';
  readonly displayName = 'Channel Mixer';
  
  // Red output = r * redR + g * redG + b * redB
  redR = 1.0;
  redG = 0.0;
  redB = 0.0;
  
  // Green output = r * greenR + g * greenG + b * greenB
  greenR = 0.0;
  greenG = 1.0;
  greenB = 0.0;
  
  // Blue output = r * blueR + g * blueG + b * blueB
  blueR = 0.0;
  blueG = 0.0;
  blueB = 1.0;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = CHANNEL_MIXER_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'redR', type: 'float', min: -2, max: 2, default: 1.0, description: 'Red → Red' },
      { name: 'redG', type: 'float', min: -2, max: 2, default: 0.0, description: 'Green → Red' },
      { name: 'redB', type: 'float', min: -2, max: 2, default: 0.0, description: 'Blue → Red' },
      { name: 'greenR', type: 'float', min: -2, max: 2, default: 0.0, description: 'Red → Green' },
      { name: 'greenG', type: 'float', min: -2, max: 2, default: 1.0, description: 'Green → Green' },
      { name: 'greenB', type: 'float', min: -2, max: 2, default: 0.0, description: 'Blue → Green' },
      { name: 'blueR', type: 'float', min: -2, max: 2, default: 0.0, description: 'Red → Blue' },
      { name: 'blueG', type: 'float', min: -2, max: 2, default: 0.0, description: 'Green → Blue' },
      { name: 'blueB', type: 'float', min: -2, max: 2, default: 1.0, description: 'Blue → Blue' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'redR': return this.redR;
      case 'redG': return this.redG;
      case 'redB': return this.redB;
      case 'greenR': return this.greenR;
      case 'greenG': return this.greenG;
      case 'greenB': return this.greenB;
      case 'blueR': return this.blueR;
      case 'blueG': return this.blueG;
      case 'blueB': return this.blueB;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'redR': this.redR = value; break;
      case 'redG': this.redG = value; break;
      case 'redB': this.redB = value; break;
      case 'greenR': this.greenR = value; break;
      case 'greenG': this.greenG = value; break;
      case 'greenB': this.greenB = value; break;
      case 'blueR': this.blueR = value; break;
      case 'blueG': this.blueG = value; break;
      case 'blueB': this.blueB = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const channelMixerWgsl = getCompiledShader('postprocess/channel-mixer');
    this.shader = ctx.createShader(channelMixerWgsl, channelMixerWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'channel-mixer-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'channel-mixer-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport
    
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    this.uniformWriter.setAll({
      redOut: [lerp(1, this.redR, intensity), lerp(0, this.redG, intensity), lerp(0, this.redB, intensity)],
      _pad0: 0,
      greenOut: [lerp(0, this.greenR, intensity), lerp(1, this.greenG, intensity), lerp(0, this.greenB, intensity)],
      _pad1: 0,
      blueOut: [lerp(0, this.blueR, intensity), lerp(0, this.blueG, intensity), lerp(1, this.blueB, intensity)],
      _pad2: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
